import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Eye, EyeOff, Loader2, CheckCircle2, AlertCircle, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const isReset = searchParams.get('reset') === '1';

  const [tokenInfo, setTokenInfo] = useState(null);
  const [validating, setValidating] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError('Aucun token fourni.');
      setValidating(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/validate-token?token=${token}`);
        setTokenInfo(data);
      } catch (e) {
        setTokenError(e.response?.data?.detail || 'Token invalide ou expire');
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return;
    if (password !== confirm) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/set-password`, { token, password });
      setSuccess(true);
    } catch (e) {
      setTokenError(e.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md" data-testid="set-password-page">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-[#002FA7] rounded-lg flex items-center justify-center mx-auto mb-3">
            <span className="text-white font-bold text-lg">SEO</span>
          </div>
          <h1 className="font-heading font-bold text-2xl text-neutral-900">
            {isReset ? 'Reinitialiser votre mot de passe' : 'Definir votre mot de passe'}
          </h1>
        </div>

        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          {validating && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#002FA7]" />
              <p className="text-neutral-500 text-sm mt-3">Verification du lien...</p>
            </div>
          )}

          {tokenError && !validating && (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-3" />
              <p className="text-red-600 font-medium">{tokenError}</p>
              <p className="text-neutral-500 text-sm mt-2">Contactez votre administrateur pour obtenir un nouveau lien.</p>
            </div>
          )}

          {success && (
            <div className="text-center py-8" data-testid="password-set-success">
              <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-3" />
              <p className="text-green-700 font-medium text-lg">Mot de passe defini avec succes !</p>
              <p className="text-neutral-500 text-sm mt-2">Vous pouvez maintenant vous connecter.</p>
              <Link to="/login">
                <Button className="bg-[#002FA7] hover:bg-[#002277] mt-6" data-testid="go-to-login-btn">
                  Se connecter
                </Button>
              </Link>
            </div>
          )}

          {tokenInfo && !success && !tokenError && (
            <>
              <div className="bg-neutral-50 rounded p-3 mb-6">
                <p className="text-sm text-neutral-600">
                  Bienvenue <strong>{tokenInfo.name}</strong>
                </p>
                <p className="text-xs text-neutral-400 mt-0.5">{tokenInfo.email}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="password">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? 'text' : 'password'}
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      required minLength={6} className="pr-10" data-testid="set-password-input" />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                      onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-neutral-400">Minimum 6 caracteres</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirm">Confirmer le mot de passe</Label>
                  <Input id="confirm" type="password" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required minLength={6} data-testid="confirm-password-input" />
                  {confirm && password !== confirm && (
                    <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277] w-full"
                  disabled={submitting || password.length < 6 || password !== confirm}
                  data-testid="submit-password-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
                  {isReset ? 'Reinitialiser' : 'Definir mon mot de passe'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
